const normalizeCfop = (value) => {
  if (!value) return '';
  const digits = String(value).replace(/[^0-9]/g, '');
  return digits.slice(0, 4);
};

const formatCfop = (value) => {
  const digits = normalizeCfop(value);
  if (!digits) return '';
  if (digits.length !== 4) return digits;
  return `${digits[0]}.${digits.slice(1)}`;
};

const normalizeText = (value) => {
  if (!value) return '';
  return value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const CFOPS_SEM_EFEITO_FINANCEIRO = new Map([
  ['5901', 'Remessa para conserto sem cobrança.'],
  ['5902', 'Remessa em garantia sem geração de cobrança.'],
  ['5904', 'Remessa para demonstração sem efeito financeiro.'],
  ['5905', 'Remessa para industrialização por encomenda sem cobrança imediata.'],
  ['5906', 'Retorno de mercadoria consignada sem cobrança.'],
  ['5910', 'Remessa em bonificação/doação não gera financeiro.'],
  ['5911', 'Remessa em comodato sem cobrança.'],
  ['5912', 'Remessa para exposição ou feira sem efeito financeiro.'],
  ['5913', 'Remessa para feiras/eventos sem efeito financeiro.'],
  ['5914', 'Remessa como brinde.'],
  ['5915', 'Remessa de mostruário.'],
  ['5916', 'Remessa para assistência técnica sem cobrança.'],
  ['5917', 'Remessa simbólica sem geração de financeiro.'],
  ['5918', 'Retorno simbólico sem financeiro.'],
  ['5919', 'Devolução simbólica sem financeiro.'],
  ['5920', 'Remessa para depósito fechado/armazém geral.'],
  ['5921', 'Retorno de depósito fechado/armazém geral.'],
  ['5922', 'Transferência de mercadoria dentro da empresa.'],
  ['5923', 'Remessa para industrialização e retorno sem cobrança imediata.'],
  ['5933', 'Remessa de produção com entrega futura (sem cobrança agora).'],
  ['5949', 'Outras saídas sem incidência financeira.'],
  ['6901', 'Remessa para conserto sem cobrança.'],
  ['6902', 'Remessa em garantia sem cobrança.'],
  ['6904', 'Remessa para demonstração sem efeito financeiro.'],
  ['6910', 'Remessa em bonificação/doação não gera financeiro.'],
  ['6911', 'Remessa em comodato sem cobrança.'],
  ['6912', 'Remessa para exposição ou feira sem efeito financeiro.'],
  ['6920', 'Remessa para depósito fechado/armazém geral.'],
  ['6921', 'Retorno de depósito/armazém geral.'],
  ['6933', 'Remessa de produção com entrega futura (sem cobrança).'],
  ['6949', 'Outras saídas sem incidência financeira.'],
]);

const CFOP_PREFIXOS_SEM_FINANCEIRO = [
  { prefix: '5', sufixo: '9', motivo: 'Remessa simbólica ou movimento interno sem financeiro.' },
  { prefix: '6', sufixo: '9', motivo: 'Remessa simbólica ou movimento interno sem financeiro.' },
];

const NATUREZAS_SEM_EFEITO_FINANCEIRO = new Map([
  ['remessa para conserto', 'Remessas para conserto não geram financeiro.'],
  ['remessa para garantia', 'Remessas em garantia não geram financeiro.'],
  ['remessa em garantia', 'Remessas em garantia não geram financeiro.'],
  ['remessa para demonstracao', 'Remessas para demonstração não geram financeiro.'],
  ['remessa para demonstrac', 'Remessas para demonstração não geram financeiro.'],
  ['remessa para exposicao', 'Remessas para exposição não geram financeiro.'],
  ['remessa para feira', 'Remessas para feira não geram financeiro.'],
  ['remessa em bonificacao', 'Bonificações não geram cobrança.'],
  ['remessa em doacao', 'Doações não geram cobrança.'],
  ['bonificacao', 'Bonificações não geram cobrança.'],
  ['doacao', 'Doações não geram cobrança.'],
  ['brinde', 'Brindes não geram cobrança.'],
  ['simples remessa', 'Simples remessa não gera financeiro.'],
  ['transferencia de mercadoria', 'Transferências internas não geram financeiro.'],
  ['remessa de mostruario', 'Remessa de mostruário não gera financeiro.'],
  ['remessa em comodato', 'Comodato não gera financeiro.'],
  ['remessa para industrializacao', 'Remessas para industrialização não geram financeiro imediato.'],
  ['industrializacao por encomenda', 'Industrialização por encomenda não gera financeiro imediato.'],
  ['remessa para deposito', 'Remessas para depósito não geram financeiro.'],
  ['retorno de deposito', 'Retorno de depósito não gera financeiro.'],
  ['retorno de mercadoria', 'Retornos não geram novo financeiro.'],
  ['amostra gratis', 'Amostras grátis não geram cobrança.'],
  ['amostra gratis sem valor comercial', 'Amostras grátis não geram cobrança.'],
  ['remessa gratuita', 'Remessas gratuitas não geram financeiro.'],
]);

export function classifyNotaFinanceira({
  cfop,
  naturezaOperacao,
  valorTotal,
  documento,
} = {}) {
  const normalizedCfop = normalizeCfop(cfop);
  const formattedCfop = formatCfop(cfop);
  const normalizedNatureza = normalizeText(naturezaOperacao);
  const valor = Number(valorTotal ?? 0);

  let geraFinanceiro = true;
  let motivoFinanceiro = '';

  if (!Number.isFinite(valor) || Math.abs(valor) < 0.00001) {
    geraFinanceiro = false;
    motivoFinanceiro = 'Valor total não informado ou igual a zero.';
  } else if (normalizedCfop && CFOPS_SEM_EFEITO_FINANCEIRO.has(normalizedCfop)) {
    geraFinanceiro = false;
    const baseMotivo = CFOPS_SEM_EFEITO_FINANCEIRO.get(normalizedCfop);
    motivoFinanceiro = `CFOP ${formattedCfop || normalizedCfop}: ${baseMotivo}`;
  } else if (normalizedCfop) {
    const regra = CFOP_PREFIXOS_SEM_FINANCEIRO.find((item) =>
      normalizedCfop.startsWith(item.prefix) && normalizedCfop[1] === item.sufixo
    );
    if (regra) {
      geraFinanceiro = false;
      motivoFinanceiro = `CFOP ${formattedCfop || normalizedCfop}: ${regra.motivo}`;
    }
  }

  if (geraFinanceiro && normalizedNatureza) {
    for (const [chave, motivo] of NATUREZAS_SEM_EFEITO_FINANCEIRO.entries()) {
      if (normalizedNatureza.includes(chave)) {
        geraFinanceiro = false;
        motivoFinanceiro = motivo;
        break;
      }
    }
  }

  if (geraFinanceiro && documento === 'cte' && normalizedNatureza.includes('cobranca reversa')) {
    geraFinanceiro = false;
    motivoFinanceiro = 'CT-e com natureza de cobrança reversa sem financeiro.';
  }

  return {
    geraFinanceiro,
    motivoFinanceiro,
    cfopNormalizado: normalizedCfop,
    cfopFormatado: formattedCfop,
  };
}

export const financialClassificationUtils = {
  normalizeCfop,
  formatCfop,
  normalizeText,
  CFOPS_SEM_EFEITO_FINANCEIRO,
  NATUREZAS_SEM_EFEITO_FINANCEIRO,
};
